import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AddressGroupV2 from 'components/field/AddressGroupV2';
import Button from 'components/field/Button';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import { FormFooter } from 'components/form/FormLayout.styled';
import FormHeader from 'components/global/FormHeader';
import Tooltip from 'components/global/Tooltip';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import { usePrompt } from 'hooks/usePrompt';

import { WAREHOUSES } from 'services/consts';

import { getPickupLocationCountries } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import * as S from './PickUpLocation.styled';

/**
 *
 * @author @muhammedburakak
 */

const PickUpLocation = () => {
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const { Country } = useLocale();
	const { showSuccess, showError } = useAlert();
	const defaultColSpan = {
		lg: 7
	};

	const { fullName: senderName, phone: mobileNumber, email, planName = null } = useSelector((state) => state.user);

	const [isLoading, setIsLoading] = useState(false);
	const [ID, setID] = useState();
	const allowedCountriesStr = useSelector((state) => state.user.localization.allowedCountries);
	const [formMode, setFormMode] = useState('add');

	const allowedCountries = useMemo(
		() => (allowedCountriesStr ? [Country, ...allowedCountriesStr.split(',')] : [Country]),
		[allowedCountriesStr]
	);
	const { track } = useAppTracking();
	const navigate = useNavigate();
	// schema of form
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					address: yup.object().shape({
						text: yup.string().nullable().required(),
						countryName: yup.string().isCountryAllowed().nullable().required(),
						stateName: yup.string().nullable(),
						cityName: yup.string().nullable().required(),
						districtName: yup.string().nullable(),
						neighbourhoodName: yup.string().nullable(),
						street: yup.string().nullable(),
						buildingName: yup.string().nullable(),
						addressInputType: yup.string().nullable(),
						zipCode: yup.string().nullable(),
						lat: yup.number().min(-90).max(90).nullable(),
						lng: yup.number().min(-180).max(180).nullable()
					}),
					email: yup.string().email().nullable().required(),
					mobileNumber: yup.string().nullable().phoneNumber().required(),
					senderName: yup.string().nullable().required(),
					warehouseName: yup.string().nullable().min(3).trim().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			address: {
				text: null,
				countryName: null,
				stateName: null,
				cityName: null,
				districtName: null,
				neighbourhoodName: null,
				street: null,
				addressInputType: null,
				buildingName: null,
				zipCode: null,
				lat: null,
				lng: null
			},
			email: null,
			senderName: null,
			mobileNumber: null,
			warehouseName: 'DefaultWH'
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const getPickUpLocation = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axios.post(WAREHOUSES.GET_PICKUP_LOCATION);

			if (response.status === 200) {
				setID(response.data.ID);

				reset({
					address: {
						text: response.data.address.text ?? null,
						countryName: response.data.address.countryName ?? Country ?? null,
						stateName: response.data.address.stateName ?? null,
						cityName: response.data.address.cityName ?? null,
						districtName: response.data.address.districtName ?? null,
						neighbourhoodName: response.data.address.neighbourhoodName ?? null,
						street: response.data.address.street ?? null,
						buildingName: response.data.address.buildingName ?? null,
						addressInputType: response.data.address.addressInputType ?? null,
						zipCode: response.data.address.zipCode ?? null,
						lat: response.data.address.lat ?? null,
						lng: response.data.address.lng ?? null
					},
					email: response.data.email ?? null,
					senderName: response.data.senderName ?? null,
					mobileNumber: response.data.mobileNumber ?? null,
					warehouseName: response.data.warehouseName ?? null
				});
				setFormMode(response?.data?.address?.text ? 'edit' : 'add');
			} else if (response.status === 204) {
				reset({
					senderName,
					email,
					mobileNumber,
					warehouseName: 'DefaultWH',
					address: {
						text: null,
						countryName: Country ?? null,
						stateName: null,
						cityName: null,
						street: null,
						addressInputType: null,
						buildingName: null,
						districtName: null,
						neighbourhoodName: null,
						zipCode: null,
						lat: null,
						lng: null
					}
				});
				setFormMode('add');
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
		}
		setIsLoading(false);
	}, [reset, Country]);

	useEffect(() => {
		getPickUpLocation();
	}, []);

	const onSubmit = (data) => {
		setIsLoading(true);
		const URL = WAREHOUSES.UPDATE_PICKUP_LOCATION;
		axios
			.post(URL, {
				ID: ID || undefined,
				address: {
					...data.address
				},
				email: data.email,
				senderName: data.senderName,
				mobileNumber: data.mobileNumber,
				warehouseName: data.warehouseName
			})
			.then((response) => {
				const { status } = response;
				setID(response.data.ID);
				if (status === 200) {
					reset({
						address: {
							...data.address,
							countryID: null,
							cityID: null
						},
						email: data.email ?? null,
						senderName: data.senderName ?? null,
						mobileNumber: data.mobileNumber ?? null,
						warehouseName: data.warehouseName ?? null
					});
					showSuccess();
					dispatch(getPickupLocationCountries({ invalidate: true }));
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return (
		<div>
			<HeaderContainerFormHeader className="settingsMaxWidth settingsFormHeader">
				<FormHeader boldTitle="pickupLocation" />
				<S.ButtonContainer>
					<Tooltip
						timeout={100}
						placement="bottom"
						header="warehouse-module"
						title="warehouseModuleDescription"
						ctaButtonLabel="viewModuleDetails"
						ctaButtonOnClick={() => {
							track('moreThan1WH');
							navigate(PATHS.WAREHOUSE_MODULE);
						}}>
						<Button label="addWarehouse" icon={faPlus} variant="outline" disabled />
					</Tooltip>
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						loading={isLoading}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						icon={faFloppyDisk}
						label="save"
						variant="blue"
						className="saveButton"
					/>
				</S.ButtonContainer>
			</HeaderContainerFormHeader>

			<S.StyledForm formMethods={formMethods} borderless>
				<S.StyledCard className="settingsMaxWidth" fullWidth>
					<FormItem
						colSpan={defaultColSpan}
						label="pickupLocationName"
						isLoading={isLoading}
						required
						toolTip="pickupLocation.pickupLocationNameTooltip">
						<StringInput name="warehouseName" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="senderName" isLoading={isLoading} required>
						<StringInput name="senderName" />
					</FormItem>
					<F.Line className="line" />
					<FormItem colSpan={defaultColSpan} label="email" isLoading={isLoading} required>
						<StringInput name="email" />
					</FormItem>
					<F.Line className="line" />
					<FormItem colSpan={defaultColSpan} label="mobileNumber" isLoading={isLoading} required>
						<PhoneNumber
							name="mobileNumber"
							onChange={(value) =>
								setValue('mobileNumber', value, { shouldValidate: true, shouldDirty: true })
							}
							value={watch('mobileNumber')}
						/>
					</FormItem>
					<F.Line className="line" />
					<AddressGroupV2
						centerAddressField
						formFieldName="address"
						disabled={isLoading}
						isLoading={isLoading}
						lineSeperator={true}
						restrictions={
							planName !== 'EnterprisePackage'
								? {
										country: {
											isNotDifferent: planName === 'FreePackage',
											code: planName === 'FreePackage' ? Country : allowedCountries
										}
									}
								: null
						}
						formMode={formMode}
					/>
				</S.StyledCard>
			</S.StyledForm>

			<FormFooter>
				<Button
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
					className="saveButton"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					loading={isLoading}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					icon={faFloppyDisk}
					label="save"
					variant="blue"
				/>
			</FormFooter>
		</div>
	);
};

export default PickUpLocation;
